// Общие кэши AI (обсуждение экономии 2026-09-11, миграция
// 20260911220000_ai_usage_and_caches.sql): ключи, готовые вопросы-подсказки,
// сборка контекста сцены/урока и «дешёвые часы» DeepSeek. Один модуль и для
// сервера (chat/actions.ts), и для ночного прогрева (scripts/warm-ai-cache.mts) —
// ключи обязаны совпадать. Поэтому импорты здесь только относительные и с .ts:
// скрипт запускается Node без сборки.

import type { LessonChatContext, SceneContext } from './deepseek'
import type { LessonContent } from './lessons/types'
import { trFold } from './lesson-chat.ts'

// ─── «Спроси Turkido» ───────────────────────────────────────────────────────

// Вопрос без регистра, диакритики, знаков препинания и лишних пробелов:
// «Как сказать спасибо?» и «как сказать  спасибо» — один ключ. Диакритика и
// пунктуация ошибкой в вопросе и так не считаются (промпт getQuestionAnswer),
// так что готовый ответ с разбором ошибки остаётся верным для любого варианта.
export function normalizeQuestion(question: string): string {
  return trFold(question.replace(/ё/g, 'е').replace(/Ё/g, 'Е'))
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Длинные вопросы почти всегда уникальны — кэшировать их незачем.
const QA_CACHE_MAX_LEN = 200

// null — вопрос не кэшируется.
export function qaCacheKey(level: string, question: string): string | null {
  const norm = normalizeQuestion(question)
  if (!norm || norm.length > QA_CACHE_MAX_LEN) return null
  // v2 (2026-09-18): ответы получили topic и тему «жизнь в Турции» — старые записи
  // кэша с отказами «не про язык» не должны всплывать.
  return `${level}|v2|${norm}`
}

// Частые вопросы новичка — кнопки в пустом окне «Спроси» (tutor-qa.tsx).
// Ответы на них заранее лежат в qa_answer_cache (прогрев для A1), поэтому
// приходят мгновенно и не тратят запрос к DeepSeek.
export const QA_SUGGESTIONS = [
  'Как сказать "спасибо" и "пожалуйста" по-турецки?',
  'Как произносить букву ı без точки?',
  'Когда окончание -lar, а когда -ler?',
  'Как сказать "у меня есть"?',
  'Чем отличаются Merhaba и Selam?',
  'Как задать вопрос с частицей mi?',
  'Как считать до десяти по-турецки?',
  'Чем отличаются bu, şu и o?',
  // О жизни в Турции (Plus/Pro, у Free два пробных в неделю).
  'Как записаться к врачу через MHRS?',
  'Как торговаться на рынке и что сказать?',
]

// ─── Первые реплики разговоров ──────────────────────────────────────────────

// Сколько разных первых реплик держим на один ключ: меньше — генерируем
// новую (и добавляем), столько — берём случайную из готовых. У сцены больше
// одной — чтобы «Сыграть заново» не начиналось каждый раз одной фразой; у
// урока разнообразие и так даёт случайное первое слово задачи (оно в ключе).
export const OPENING_VARIANTS = 3
export const LESSON_OPENING_VARIANTS = 1

export function sceneOpeningBase(scene: string): string {
  return `scene:${scene}`
}

// Первая реплика разговора по уроку подводит к ПЕРВОМУ слову задачи — от
// него и ключ (getOpeningMessage строит вопрос именно под это слово).
export function lessonOpeningBase(lessonSlug: string, firstTaskWord: string): string {
  return `lesson:${lessonSlug}:${trFold(firstTaskWord)}`
}

export function sceneContextFrom(
  meta: { title: string; role: string; goal: string; learner?: string },
  content: LessonContent | null | undefined,
): SceneContext {
  const phrases = (content?.vocabulary ?? []).map((v) => v.turkish)
  return { title: meta.title, role: meta.role, goal: meta.goal, learner: meta.learner, phrases }
}

export function lessonContextFrom(topic: string, content: LessonContent, taskWords: string[]): LessonChatContext {
  const words = (content.vocabulary ?? [])
    .filter((v) => v.grammarTag !== 'alphabet')
    .map((v) => ({ tr: v.turkish, ru: v.russian }))
  const intuitive = content.grammarNote?.intuitive?.ru?.replace(/\s+/g, ' ').trim() ?? ''
  const scenario = (content.dialogue?.short ?? []).slice(0, 8).map((l) => ({ speaker: l.speaker, tr: l.tr, ru: l.ru }))
  return { topic, words, taskWords, grammarHint: intuitive ? intuitive.slice(0, 280) : null, scenario }
}

// ─── Дешёвые часы DeepSeek ──────────────────────────────────────────────────

// Прайс на 2026-09-11 (api-docs.deepseek.com/quick_start/pricing): пик —
// пн–пт 01:00–04:00 и 06:00–10:00 UTC, в остальное время всё вдвое дешевле.
// Фоновый прогрев кэшей запускается только вне пика.
export function isDeepSeekOffPeak(at: Date = new Date()): boolean {
  const day = at.getUTCDay() // 0 — вс, 6 — сб
  if (day === 0 || day === 6) return true
  const h = at.getUTCHours()
  return !((h >= 1 && h < 4) || (h >= 6 && h < 10))
}
